import { ChangeEvent, useEffect, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import {
  BadgeCounter,
  CloseIcon,
  Box as DarkBox,
  IconButton,
  SearchIcon,
  TextInput,
} from '@hivewatch/dark'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeSOPIncidentDrawer,
  selectSOPIncidentDrawerState,
  setSOPIncidentNameLike,
} from '@/redux/ui/uiSlice'
import { SEARCH, trackEvent } from '@/utils/analytics'

import { SOP_INCIDENT_DRAWER_CLOSE } from '../../../shared/constants/analyticsEvents'

interface SOPIncidentHeaderIProps {
  facilityId: string
  incidentId?: string
  sopsCount: number
}

export const SOPIncidentHeader = ({
  facilityId,
  incidentId,
  sopsCount,
}: SOPIncidentHeaderIProps) => {
  const dispatch = useAppDispatch()
  const colorScheme = useLightMode()
  const { t } = useTranslation()

  const { sopNameLike } = useAppSelector(selectSOPIncidentDrawerState)
  const [nameFilter, setNameFilter] = useState(sopNameLike)
  const [debouncedNameFilter] = useDebounce(nameFilter, 1000)

  useEffect(() => {
    dispatch(setSOPIncidentNameLike(debouncedNameFilter))
    if (debouncedNameFilter) {
      trackEvent(`${SEARCH} SOP Incident Drawer`, {
        search_term: debouncedNameFilter,
        facility_id: facilityId,
        incident_id: incidentId,
      })
    }
  }, [debouncedNameFilter])

  const onNameFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setNameFilter(e.target.value)
  }

  const onClose = () => {
    trackEvent(SOP_INCIDENT_DRAWER_CLOSE, {
      facility_id: facilityId,
      incident_id: incidentId,
      closed_via: 'close_button',
    })
    dispatch(closeSOPIncidentDrawer())
  }

  const prefix = colorScheme === 'light' ? 'light.' : ''
  return (
    <DarkBox
      alignItems='center'
      display='flex'
      gap='12px'
      height='64px'
      pb='16px'
      pt='16px'
      px='48px'
    >
      <DarkBox
        as='span'
        color={`${prefix}text.primary`}
        fontFamily="'Soehne Kraftig', 'Söhne Kräftig', sans-serif"
        fontSize='20px'
        fontWeight='500'
        lineHeight='28px'
        whiteSpace='nowrap'
      >
        {t('incidents:sop_title')}
      </DarkBox>
      <BadgeCounter
        colorScheme={colorScheme}
        count={sopsCount ?? 0}
        variant='button'
      />
      <DarkBox flex='1' minWidth={0} />
      <TextInput
        colorScheme={colorScheme}
        leftIcon={<SearchIcon colorScheme={colorScheme} size={14} />}
        onChange={onNameFilterChange}
        placeholder={t('common:search')}
        value={nameFilter ?? ''}
        width='220px'
      />
      <IconButton
        _active={{ bg: 'transparent' }}
        aria-label='close SOP drawer'
        colorScheme={colorScheme}
        dimension={36}
        hoverBgColor='transparent'
        icon={<CloseIcon size={11} />}
        onClick={onClose}
      />
    </DarkBox>
  )
}
